import { Icon, Intent } from '@blueprintjs/core';
import { Button } from 'components/buttons';
import { FormSelect, FormTimePicker } from 'components/form-fields';
import { ShowHide } from 'components/layout';
import { Table } from 'components/listing';
import { NotificationInline, NotificationToaster } from 'components/notifications';
import { Tooltip } from 'components/tooltip';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { ManualEntryService } from 'services';

const defaultArray = [];

export function DetailedManualEntry({
    originalActivities = defaultArray,
    onValidChange,
    onActivitiesChange,
    editMode = false
}) {

    const timeFormat = "HH:mm";
    const minutesInDay = 1440;
    const [loadingOptions, setLoadingOptions] = useState(true);
    const [loadedActivities, setLoadedActivities] = useState(false);

    const init = () => {
        ManualEntryService.getOptions().then(function (optionsResponse) {
            setActivityOptions(optionsResponse.activityTypes.map((o) => { return { id: o.key, name: o.name } }) ?? []);
            setLoadingOptions(false);
        }, function () {
            NotificationToaster.show("danger", "Could not load settings, please refresh the page");
        });
    }

    const [activityOptions, setActivityOptions] = useState([]);
    const [activities, setActivities] = useState([]);
    const [rows, setRows] = useState([]);
    const [isValid, setIsValid] = useState(false);
    const [traceTooLong, setTraceTooLong] = useState(false);

    useEffect(function () {
        if (originalActivities.length > 0 && !loadedActivities) {
            setActivities(originalActivities);
            setLoadedActivities(true);
        }
    }, [originalActivities]);

    useEffect(init, []);

    const headerData = {
        headers: [
            { key: "activity", value: "Activity" },
            { key: "start-time", value: "Start Time" },
            { key: "end-time", value: "End Time" },
            { key: "duration", value: "Duration" },
            { key: "insert", value: "Insert" },
            { key: "delete", value: "Delete" },
        ]
    }

    const onActivityOptionSelect = (selectedItem) => {
        insertActivityRow(selectedItem.id);
    }

    const insertActivityRow = function (activityType = null, index = null) {
        let newActivities = [...activities];

        let activity = {
            type: activityType,
            time: null,
            duration: null
        }

        if (index != null) {
            newActivities.splice(index, 0, activity);
        } else {
            newActivities.push(activity);
        }

        mapAndSetActivities(newActivities);
    }

    const deleteActivityRow = function (index) {
        let newActivities = [...activities];

        newActivities.splice(index, 1);

        mapAndSetActivities(newActivities);
    }

    const onActivitySelect = function (activity, index) {
        let newActivities = [...activities];

        newActivities[index].type = activity.id;
        mapAndSetActivities(newActivities);
    }

    const mapAndSetActivities = function (newActivities) {
        newActivities = recalculateTimings(newActivities);
        setActivities(newActivities);
    }

    const onStartDateChange = function (date, index) {
        let newActivities = [...activities];
        newActivities[index].time = moment(date).format(timeFormat);
        mapAndSetActivities(newActivities);
    }

    const onEndDateChange = function (date, index) {
        let newActivities = [...activities];

        const startTime = timeStringToNumeric(newActivities[index].time);
        let endTime = timeStringToNumeric(moment(date).format(timeFormat));

        if (endTime < startTime) {
            endTime += minutesInDay;
        }

        newActivities[index].duration = endTime - startTime;
        mapAndSetActivities(newActivities);
    }

    const onDurationChange = function (date, index) {
        let newActivities = [...activities];
        newActivities[index].duration = timeStringToNumeric(moment(date).format(timeFormat));
        mapAndSetActivities(newActivities);
    }


    const renderRows = function () {

        const timeToMoment = function (time) {

            const momentTime = moment(time, timeFormat);

            if (time == null || !momentTime.isValid()) {
                return null;
            }

            return momentTime;
        }

        let newRows = [];
        let newDay = false;
        let rowsValid = true;
        let totalDuration = 0;

        activities.forEach((activity, index) => {

            const startTime = timeStringToNumeric(activity.time);
            const endTime = startTime + activity.duration;

            const nextDay = (endTime > minutesInDay && startTime <= minutesInDay) || (startTime === 0 && activity.duration > 0 && index > 0);

            totalDuration += activity.duration ?? 0;

            if (activity.type == null || activity.time == null || activity.time === "" || activity.duration === 0 || activity.duration == null) {
                rowsValid = false;
            }

            //We are going over midnight, insert a row
            if (nextDay) {
                newRows.push({
                    key: "new-day-row",
                    class: "heading",
                    cells: [
                        { colSpan: 6, content: "New Day" }
                    ]
                });
            }

            newDay = newDay || nextDay;

            const endTimeString = timeToString(endTime);
            const durationTime = timeToString(activity.duration);

            let disallowedActivities = [];

            if (index > 0 && activities[index - 1].type?.length > 0) {
                disallowedActivities.push(activities[index - 1].type);
            }

            if (index <= activities.length - 2 && activities[index + 1].type?.length > 0) {
                disallowedActivities.push(activities[index + 1].type);
            }

            const rowAvailableActivities = activityOptions.map((a) => {
                return {
                    ...a,
                    disabled: disallowedActivities.includes(a.id)
                }
            })

            newRows.push({
                key: "activity-row-" + index,
                class: newDay ? "primary" : "",
                cells: [
                    <div className='inline-items' key="activity-select-control">
                        <FormSelect placeholder='Please select an activity' items={rowAvailableActivities} onItemSelect={(a) => onActivitySelect(a, index)} selectedValue={activity.type} />
                        <ShowHide
                            evaluator={activity.type == null}
                            show={<Tooltip content="Activity must be set"><Icon icon="error" intent={Intent.DANGER} /></Tooltip>}
                        />
                    </div>,
                    (index === 0 ? <FormTimePicker
                        value={timeToMoment(activity.time)?.format()}
                        onChange={(item) => { onStartDateChange(item, index) }}
                        showArrowButtons={false}
                    /> : activity.time),
                    <FormTimePicker
                        key="activity-end-time-control"
                        value={timeToMoment(endTimeString)?.format()}
                        onChange={(item) => { onEndDateChange(item, index) }}
                        showArrowButtons={false}
                    />,
                    <div className='inline-items' key="activity-duration-time-control">
                        <FormTimePicker
                            value={timeToMoment(durationTime)?.format()}
                            onChange={(item) => { onDurationChange(item, index) }}
                            showArrowButtons={false}
                        />
                        <ShowHide
                            evaluator={activity.duration == null || activity.duration === 0}
                            show={<Tooltip content="Duration must be set"><Icon icon="error" intent={Intent.DANGER} /></Tooltip>}
                        />
                    </div>,

                    <Button key="activity-btn-add" large={false} iconOnly icon="plus" minimal onClick={() => insertActivityRow(null, index + 1)} disabled={index + 1 === activities.length} />,
                    <Button key="activity-btn-delete" large={false} iconOnly icon="trash" minimal onClick={() => deleteActivityRow(index)} />,
                ]
            })
        });

        const lastActivity = activities.length > 0 ? activities[activities.length - 1].type : null;

        const availableActivities = activityOptions.map((a) => {
            return {
                ...a,
                disabled: a.id === lastActivity
            }
        })

        const addNewItemRow = {
            key: "dummy-controls",
            class: newDay ? "primary" : "",
            cells: [
                <FormSelect key="activity-select-control" placeholder='Please select an activity' items={availableActivities} onItemSelect={onActivityOptionSelect} selectedValue={null} />,
                {
                    colSpan: 5,
                    content: "",
                }
            ]

        }

        newRows.push(addNewItemRow);

        setRows(newRows);

        const traceOver24Hours = totalDuration > minutesInDay;
        const validTrace = !traceOver24Hours && rowsValid && activities.length > 0;
        setTraceTooLong(traceOver24Hours);
        setIsValid(validTrace);

        if (validTrace) {
            onActivitiesChange(activities);
        }
    };

    useEffect(renderRows, [activities, activityOptions]);

    useEffect(function () {
        onValidChange(isValid);
    }, [isValid]);

    const recalculateTimings = function (currentActivities) {

        if (currentActivities.length === 0) {
            return currentActivities;
        }

        let prevStart = timeStringToNumeric(currentActivities[0].time);
        let prevDuration = 0;

        for (let i = 0; i < currentActivities.length; i++) {

            const currentStart = prevStart + prevDuration;
            const currentDuration = currentActivities[i].duration ?? 0;

            currentActivities[i].time = timeToString(currentStart);

            prevStart = currentStart;
            prevDuration = currentDuration;
        }

        return currentActivities;
    }

    const timeStringToNumeric = ManualEntryService.timeStringToNumeric;
    const timeToString = ManualEntryService.timeToString;

    return (
        <>
            <h2>{editMode ? "Edit Template" : "Build Template"}</h2>
            <NotificationInline intent='warning' show={traceTooLong} allowClose={false} text="Your detailed entry is too long, the maximum length it can be is 24 hours" />
            <Table data={rows} headerData={headerData} loadingData={loadingOptions} />
        </>
    );
}

DetailedManualEntry.propTypes = {
    originalActivities: PropTypes.array,
    onValidChange: PropTypes.func.isRequired,
    onActivitiesChange: PropTypes.func.isRequired,
    editMode: PropTypes.bool
};